// Enhanced use-toast.ts - Sonner-backed implementation preserving existing API
import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { toast as sonnerToast } from 'sonner';
import { DialogToastRefContext } from './DialogToastContext';

// Keep existing types for API compatibility
type ToasterToast = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
  inViewport?: React.RefObject<HTMLElement | null>;
  className?: string;
  [key: string]: unknown;
};

// Sentry configuration (can be customized)
const SENTRY_CONFIG = {
  enabled: true,
  reportDestructive: true,
  reportOnVariants: ['destructive'], // Add other variants if needed
};

// Simple function to check if toast should be reported
const shouldReportToSentry = (toast: ToasterToast): boolean => {
  if (!SENTRY_CONFIG.enabled) return false;

  return SENTRY_CONFIG.reportOnVariants.includes(toast.variant || 'default');
};

// Function to report to Sentry
const reportToastToSentry = (toast: ToasterToast) => {
  if (!shouldReportToSentry(toast)) return;

  const title =
    typeof toast.title === 'string' ? toast.title : 'Toast notification';
  const description =
    typeof toast.description === 'string' ? toast.description : undefined;

  // Add breadcrumb for tracking user flow (breadcrumbs are always useful)
  Sentry.addBreadcrumb({
    message: `Toast: ${title}`,
    category: 'ui.toast',
    level: toast.variant === 'destructive' ? 'error' : 'warning',
    data: {
      id: toast.id,
      variant: toast.variant,
      title,
      description,
      timestamp: new Date().toISOString(),
    },
  });

  // Don't capture generic toasts as separate Sentry messages — they create
  // noise without actionable context. The breadcrumb above is sufficient
  // to see toasts in the context of actual errors.
};

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

// ===== Imperative dialog toasts =====
// When `inViewport` is set, the toast is rendered directly into that DOM
// element instead of going through Sonner. No wrapper component needed —
// just pass a ref that is attached to a DOM node inside the dialog.
const DIALOG_TOAST_DURATION = 5000;
const TOAST_CONTAINER_ATTR = 'data-dialog-toasts';

// Variant classes reused from the existing Toast component (Toast.tsx) so
// they are already present in the CSS output and support dark mode.
const variantClasses: Record<string, string> = {
  default: 'border bg-background text-foreground',
  destructive: 'border-destructive bg-destructive text-destructive-foreground',
  success:
    'border-green-500 bg-green-500 text-white dark:border-green-900 dark:bg-green-900',
  warning:
    'border-yellow-500 bg-yellow-500 text-white dark:border-yellow-900 dark:bg-yellow-900',
  info: 'border-blue-500 bg-blue-500 text-white dark:border-blue-900 dark:bg-blue-900',
};

function ensureToastContainer(parent: HTMLElement): HTMLDivElement {
  let container = parent.querySelector(
    `[${TOAST_CONTAINER_ATTR}]`,
  ) as HTMLDivElement | null;

  if (!container) {
    container = document.createElement('div');
    container.setAttribute(TOAST_CONTAINER_ATTR, '');
    container.className = 'flex flex-col gap-2';
    parent.prepend(container);
  }
  return container;
}

function showDialogToast(toastData: ToasterToast): () => void {
  const parent = toastData.inViewport?.current;
  if (!parent) {
    // Ref not attached yet — fall back to Sonner
    showSonnerToast(toastData);
    return () => sonnerToast.dismiss(toastData.id);
  }

  const container = ensureToastContainer(parent);

  // Build toast element
  const el = document.createElement('div');
  const vc =
    variantClasses[toastData.variant || 'default'] || variantClasses.default;
  el.className = `rounded-lg border p-4 shadow-lg flex items-start gap-3 animate-in fade-in duration-300 ${vc}`;

  // Content wrapper
  const content = document.createElement('div');
  content.style.cssText = 'flex:1;min-width:0';

  if (toastData.title && typeof toastData.title === 'string') {
    const titleEl = document.createElement('p');
    titleEl.className = 'text-sm font-semibold';
    titleEl.style.margin = '0';
    titleEl.textContent = toastData.title;
    content.appendChild(titleEl);
  }
  if (toastData.description && typeof toastData.description === 'string') {
    const descEl = document.createElement('p');
    descEl.className = 'text-sm';
    descEl.style.cssText = `opacity:0.9;margin:${toastData.title ? '4px' : '0'} 0 0`;
    descEl.textContent = toastData.description;
    content.appendChild(descEl);
  }
  el.appendChild(content);

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.style.cssText =
    'flex-shrink:0;padding:4px;border-radius:4px;border:none;background:transparent;color:inherit;opacity:0.5;cursor:pointer;line-height:1;font-size:14px';
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.opacity = '1';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.opacity = '0.5';
  });
  el.appendChild(closeBtn);

  // Dismiss logic
  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    el.remove();
    if (container.children.length === 0) container.remove();
  };

  closeBtn.addEventListener('click', dismiss);
  container.appendChild(el);

  const timer = setTimeout(dismiss, DIALOG_TOAST_DURATION);

  return () => {
    clearTimeout(timer);
    dismiss();
  };
}

type Toast = Omit<ToasterToast, 'id'>;

type ToastVariant = NonNullable<ToasterToast['variant']>;

// Sonner only applies rich colors to the toast body and close button — its
// built-in action button stays a neutral dark pill for every variant. Re-style
// that button per variant so the CTA harmonizes with the toast it sits on:
// a solid fill in the variant's accent (reusing Sonner's own rich-color vars,
// so it tracks light/dark for free), and the app's primary token for the
// default toast so it reads as an on-brand call to action. Custom ReactNode
// actions (e.g. the man-overboard toast) render verbatim and are unaffected.
const ACTION_BUTTON_STYLE_BY_VARIANT: Record<
  ToastVariant,
  React.CSSProperties
> = {
  default: {
    backgroundColor: 'hsl(var(--primary))',
    color: 'hsl(var(--primary-foreground))',
  },
  destructive: {
    backgroundColor: 'var(--error-text)',
    color: 'var(--error-bg)',
  },
  warning: {
    backgroundColor: 'var(--warning-text)',
    color: 'var(--warning-bg)',
  },
  success: {
    backgroundColor: 'var(--success-text)',
    color: 'var(--success-bg)',
  },
  info: { backgroundColor: 'var(--info-text)', color: 'var(--info-bg)' },
};

// Map variant to sonner toast method
function showSonnerToast(props: ToasterToast): string | number {
  const { id, title, description, variant, className, inViewport, ...rest } =
    props;
  const opts = {
    id,
    description: description as string | undefined,
    className,
    closeButton: true,
    actionButtonStyle: ACTION_BUTTON_STYLE_BY_VARIANT[variant ?? 'default'],
    ...rest,
  };

  switch (variant) {
    case 'destructive':
      return sonnerToast.error(title as string, opts);
    case 'success':
      return sonnerToast.success(title as string, opts);
    case 'warning':
      return sonnerToast.warning(title as string, opts);
    case 'info':
      return sonnerToast.info(title as string, opts);
    default:
      return sonnerToast(title as string, opts);
  }
}

function toast({ ...props }: Toast) {
  const id = genId();
  const toastData: ToasterToast = { ...props, id };

  reportToastToSentry(toastData);

  let dismissFn: () => void;

  if (props.inViewport) {
    dismissFn = showDialogToast(toastData);
  } else {
    showSonnerToast(toastData);
    dismissFn = () => sonnerToast.dismiss(id);
  }

  const update = (updateProps: ToasterToast) => {
    if (!props.inViewport) {
      showSonnerToast({ ...updateProps, id });
    }
  };

  return { id, dismiss: dismissFn, update };
}

function useToast() {
  const dialogRef = React.useContext(DialogToastRefContext);

  const dialogAwareToast = React.useCallback(
    (props: Toast) => {
      if (props.variant === 'destructive' && dialogRef?.current) {
        return toast({ ...props, inViewport: dialogRef });
      }
      return toast(props);
    },
    [dialogRef],
  );

  return {
    toasts: [] as ToasterToast[],
    toast: dialogRef ? dialogAwareToast : toast,
    dismiss: (toastId?: string) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        sonnerToast.dismiss();
      }
    },
  };
}

export { useToast, toast, type ToasterToast };
